import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DenominationCalculator } from './DenominationCalculator';
import { DenominationData } from '@/app/types/dayEndClosure';
import { Loader2, AlertCircle, CheckCircle, IndianRupee } from 'lucide-react';
import dayEndClosureService, { 
  OpeningBalancePreview, 
  DiscrepancyPreview 
} from '@/app/services/dayEndClosureService';
import { toast } from 'sonner';

interface OpenDayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: number;
  onSuccess: () => void;
}

export const OpenDayDialog: React.FC<OpenDayDialogProps> = ({
  open,
  onOpenChange,
  siteId,
  onSuccess
}) => {
  const [step, setStep] = useState<'count' | 'review' | 'confirm'>('count');
  const [loading, setLoading] = useState(false);
  const [denominationData, setDenominationData] = useState<DenominationData>({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [notes, setNotes] = useState('');
  
  // Preview data
  const [openingPreview, setOpeningPreview] = useState<OpeningBalancePreview | null>(null);
  const [discrepancyPreview, setDiscrepancyPreview] = useState<DiscrepancyPreview | null>(null);

  // Fetch opening balance preview when dialog opens
  useEffect(() => {
    const fetchOpeningPreview = async () => {
      if (open) {
        setLoading(true);
        try {
          const preview = await dayEndClosureService.previewOpeningBalance(siteId);
          setOpeningPreview(preview);
        } catch (error) {
          toast.error('Failed to fetch opening balance preview');
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchOpeningPreview();
  }, [open, siteId]);

  // Calculate discrepancy preview when amount changes
  useEffect(() => {
    const calculateDiscrepancy = async () => {
      if (totalAmount > 0 && open) {
        try {
          const preview = await dayEndClosureService.previewDiscrepancy(
            'opening',
            siteId,
            denominationData,
            notes
          );
          setDiscrepancyPreview(preview);
        } catch (error) {
          console.error('Failed to calculate discrepancy:', error);
        }
      } else {
        setDiscrepancyPreview(null);
      }
    };

    // Debounce the calculation
    const timer = setTimeout(calculateDiscrepancy, 500);
    return () => clearTimeout(timer);
  }, [totalAmount, denominationData, notes, siteId, open]);

  const handleProceedToReview = () => {
    setStep('review');
  };

  const handleBackToCount = () => {
    setStep('count');
  };

  const handleOpenDayWithCount = async () => {
    setLoading(true);
    try {
      await dayEndClosureService.openDayWithCount(siteId, {
        denomination_data: denominationData,
        notes: notes || 'Opening count recorded'
      });
      
      toast.success('Day opened and opening count recorded successfully!');
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(dayEndClosureService.getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('count');
    setDenominationData({});
    setTotalAmount(0);
    setNotes('');
    setDiscrepancyPreview(null);
    onOpenChange(false);
  };

  const expectedAmount = openingPreview?.expected_opening_balance || 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {step === 'count' ? (
          <>
            <DialogHeader>
              <DialogTitle>Open Business Day</DialogTitle>
              <DialogDescription>
                Count your opening cash balance before starting the day.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Show expected balance upfront */}
              {openingPreview && (
                <Alert>
                  <IndianRupee className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-medium">
                        Expected opening balance: ₹{expectedAmount.toLocaleString('en-IN')}
                      </p>
                      {openingPreview.previous_closing_balance !== undefined && (
                        <p className="text-sm text-muted-foreground">
                          Based on previous day's closing balance
                        </p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <DenominationCalculator
                onTotalChange={(total, denoms) => {
                  setTotalAmount(total);
                  setDenominationData(denoms);
                }}
                expectedAmount={expectedAmount}
                title="Opening Cash Count"
              />

              {/* Live discrepancy preview */}
              {discrepancyPreview && (
                <Alert variant={
                  discrepancyPreview.discrepancy_type === 'shortage' ? 'destructive' : 
                  discrepancyPreview.discrepancy_type === 'overage' ? 'default' : 
                  'default'
                }>
                  {discrepancyPreview.discrepancy_type === 'balanced' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>
                    {discrepancyPreview.discrepancy_type === 'balanced' ? (
                      'Your count matches the expected balance perfectly!'
                    ) : (
                      <>
                        {discrepancyPreview.discrepancy_type === 'shortage' ? 'Shortage' : 'Overage'} 
                        {' '}of ₹{Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')} detected.
                        {discrepancyPreview.requires_adjustment && ' An adjustment voucher will be created.'}
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes about the opening count..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleProceedToReview} 
                disabled={loading || totalAmount === 0}
              >
                Next: Review & Open Day
              </Button>
            </DialogFooter>
          </>
        ) : step === 'review' ? (
          <>
            <DialogHeader>
              <DialogTitle>Review Count</DialogTitle>
              <DialogDescription>
                Review your count before proceeding to final confirmation.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <h3 className="font-medium">Opening Summary</h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Expected Balance:</span>
                    <span>₹{expectedAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Your Count:</span>
                    <span className="font-medium">₹{totalAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-medium">
                    <span>Discrepancy:</span>
                    <span className={
                      !discrepancyPreview ? '' :
                      discrepancyPreview.discrepancy_type === 'shortage' ? 'text-red-600' : 
                      discrepancyPreview.discrepancy_type === 'overage' ? 'text-amber-600' : 
                      'text-green-600'
                    }>
                      {!discrepancyPreview ? 'Calculating...' :
                        discrepancyPreview.discrepancy_type === 'balanced' ? 'Balanced' :
                        `₹${Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')} ${
                          discrepancyPreview.discrepancy_type === 'shortage' ? 'Short' : 'Over'
                        }`
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* What will happen */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Once you confirm:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>A new business day will be opened</li>
                    <li>Your opening count of ₹{totalAmount.toLocaleString('en-IN')} will be recorded</li>
                    {discrepancyPreview?.requires_adjustment && (
                      <li>An adjustment voucher will be created for the {discrepancyPreview.discrepancy_type}</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleBackToCount}>Back</Button>
              <Button 
                onClick={() => setStep('confirm')} 
                disabled={loading}
              >
                Proceed to Confirmation
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Final Confirmation</DialogTitle>
              <DialogDescription>
                Please confirm the details before opening the business day.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Final Summary Box */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-4">
                <h3 className="font-semibold text-lg">Opening Day Summary</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Expected Balance</p>
                    <p className="text-lg font-medium">₹{expectedAmount.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Your Count</p>
                    <p className="text-lg font-medium">₹{totalAmount.toLocaleString('en-IN')}</p>
                  </div>
                </div>

                {discrepancyPreview && (
                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-600">Status</p>
                    <p className={
                      discrepancyPreview.discrepancy_type === 'balanced' 
                        ? 'text-green-700 font-medium' 
                        : discrepancyPreview.discrepancy_type === 'shortage' 
                        ? 'text-red-700 font-medium' 
                        : 'text-amber-700 font-medium'
                    }>
                      {discrepancyPreview.discrepancy_type === 'balanced' 
                        ? '✓ Perfectly Balanced'
                        : `${discrepancyPreview.discrepancy_type === 'shortage' ? '⚠ Shortage' : '⚠ Overage'}: ₹${Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')}`
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Actions to be taken */}
              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <p className="font-medium mb-2">The following actions will be taken:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>A new business day will be opened</li>
                    <li>Opening count of ₹{totalAmount.toLocaleString('en-IN')} will be recorded</li>
                    {discrepancyPreview?.requires_adjustment && (
                      <li className="font-medium">
                        An adjustment voucher for ₹{Math.abs(discrepancyPreview.discrepancy).toLocaleString('en-IN')} 
                        {discrepancyPreview.discrepancy_type === 'shortage' ? ' shortage' : ' overage'} will be created
                      </li>
                    )}
                    {notes && <li>Your notes will be saved with this record</li>}
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Confirmation statement */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  By clicking "Confirm & Open Day", you acknowledge that you have physically counted 
                  ₹{totalAmount.toLocaleString('en-IN')} and confirm this amount as the opening balance.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep('review')}>Back to Review</Button>
              <Button 
                onClick={handleOpenDayWithCount} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm & Open Day
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};